import 'braid-design-system/reset';
import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { useHubble } from '@seek/hubble';
import type { Logger } from '@seek/logger';
import { useMelwaysInfo } from '@seek/melways-react';
import { adapter } from '@seek/tealium-adapter';
import { VocabProvider } from '@vocab/react';
import { BraidProvider, ToastProvider } from 'braid-design-system';
import seekJobs from 'braid-design-system/themes/seekJobs';
import { StrictMode, useEffect } from 'react';

import { DatadogRumProvider } from 'src/App/providers/DatadogRumProvider/DatadogRumProvider';
import type { Config } from 'src/types/types';

import { MainLayout } from './MainLayout';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { ApolloProviderWrapper } from './providers/ApolloProviderWrapper/ApolloProviderWrapper';
import { AppConfigProvider } from './providers/AppConfigProvider';
import { ServerLogProvider } from './providers/ServerLogProvider';

interface AppProps {
  config: Config;
  apolloClient: ApolloClient<NormalizedCacheObject>;
  serverLogger?: Logger;
  serverXRequestId?: string;
}

export const App = ({
  config,
  apolloClient,
  serverLogger,
  serverXRequestId,
}: AppProps) => {
  const { locale } = useMelwaysInfo();
  const hubble = useHubble();

  // Initialise analytics client with hubble instance (client-side only)
  useEffect(() => {
    adapter({ hubble });
  }, [hubble]);

  return (
    <StrictMode>
      <ServerLogProvider serverLogger={serverLogger}>
        <BraidProvider theme={seekJobs}>
          <ToastProvider>
            <AppConfigProvider config={config}>
              <DatadogRumProvider>
                <ApolloProviderWrapper
                  serverXRequestId={serverXRequestId}
                  apolloClient={apolloClient}
                >
                  <VocabProvider language={locale}>
                    <ErrorBoundary>
                      <MainLayout />
                    </ErrorBoundary>
                  </VocabProvider>
                </ApolloProviderWrapper>
              </DatadogRumProvider>
            </AppConfigProvider>
          </ToastProvider>
        </BraidProvider>
      </ServerLogProvider>
    </StrictMode>
  );
};
